import { forwardRef, useId } from 'react';
import { Label } from '@/components/atoms/Label';
import { Input } from '@/components/atoms/Input';
import type { FormFieldProps } from './FormField.types';
import styles from './FormField.module.scss';

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      label,
      error,
      helperText,
      required = false,
      labelExtra,
      id,
      disabled,
      ...inputProps
    },
    ref
  ) => {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const errorId = error ? `${fieldId}-error` : undefined;
    const helperId = helperText ? `${fieldId}-helper` : undefined;

    const ariaDescribedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

    return (
      <div className={styles.formField}>
        <div className={styles.formField__labelRow}>
          <Label htmlFor={fieldId} required={required} disabled={disabled}>
            {label}
          </Label>
          {labelExtra && <div className={styles.formField__labelExtra}>{labelExtra}</div>}
        </div>

        <Input
          ref={ref}
          id={fieldId}
          hasError={!!error}
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          {...inputProps}
        />

        {helperText && !error && (
          <p id={helperId} className={styles.formField__helper}>
            {helperText}
          </p>
        )}

        {error && (
          <p id={errorId} className={styles.formField__error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
